import React, {useState, useEffect, useMemo} from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import {Box, Typography, Link, Button, IconButton} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Protected from "../../components/Protected";
import InlineToggleCell from "../../components/InlineToggleCell";
import {ROLES} from "../../config/roles";

const PrizeCategoriesPage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        apiService.get('/api/admin/prizes/categories')
            .then(data => setCategories(data))
            .catch(err => setError(err.message || 'Failed to fetch categories'))
            .finally(() => setLoading(false));
    }, []);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 50,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                Cell: ({cell, row}) => (
                    <Link component={RouterLink} to={`/prize-categories/${row.original.id}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 200,
            },
            {
                accessorKey: 'sortOrder',
                header: 'Sort Order',
                size: 50,
            },
            {
                accessorKey: 'active',
                header: 'Status',
                size: 50,
                Cell: ({row}) => (
                    <InlineToggleCell
                        protectedBy={PrizeCategoriesPage}
                        protectAction="update"
                        value={row.original.active}
                        save={(next) => apiService.put(
                            `/api/admin/prizes/categories/${row.original.id}/active`,
                            {value: next},
                        )}
                        onSaved={(updated) => setCategories(prev =>
                            prev.map(c => c.id === updated.id ? updated : c)
                        )}
                        tooltip="Click to toggle active"
                    />
                ),
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data: categories,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {
            sorting: [{id: 'sortOrder', desc: false}], // Default sort
        },
        enableRowSelection: true,
        //onRowSelectionChange: setRowSelection,
        enableRowActions: true,
        displayColumnDefOptions: {
            'mrt-row-select': {header: '', size: 20,},
            'mrt-row-actions': {header: 'Actions', size: 100,}
        },
        positionActionsColumn: 'last',
        positionGlobalFilter: 'right',
        // Add a "Create" button to the top toolbar
        renderTopToolbarCustomActions: () => (
            <Protected component={PrizeCategoriesPage} action="create">
                <Button
                    component={RouterLink}
                    to="/prize-categories/new"
                    variant="contained"
                    startIcon={<AddIcon/>}
                >
                    Create Category
                </Button>
            </Protected>
        ),
        // Add an "Edit" action to each row
        renderRowActions: ({row}) => (
            <IconButton component={RouterLink} to={`/prize-categories/${row.original.id}`}>
                <EditIcon/>
            </IconButton>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>
                Prize Categories
            </Typography>
            <MaterialReactTable table={table}/>
        </Box>
    );
};

PrizeCategoriesPage.abilities = {
    read:   [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};


export default PrizeCategoriesPage;